import React, { useEffect, useState } from 'react';
import { apiCall } from '../AuthContext';
import { ShieldCheck, CheckCircle2, XCircle, Copy, Check, Loader2, AlertTriangle, Store } from 'lucide-react';

export default function ShopifyReadiness() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState('');

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const r = await apiCall('/api/shopify/readiness', { method: 'GET' });
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load readiness check');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const copy = (key, val) => {
    navigator.clipboard.writeText(val);
    setCopied(key);
    setTimeout(() => setCopied(''), 1500);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#0052FF]" /></div>
  );

  if (err) return (
    <div className="max-w-2xl p-4 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm" data-testid="readiness-error">
      <AlertTriangle className="w-4 h-4 inline -mt-0.5 mr-1" /> {err}
    </div>
  );

  const pd = data?.partner_dashboard || {};

  return (
    <div className="space-y-6 max-w-4xl" data-testid="shopify-readiness-page">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-xl bg-emerald-100 text-emerald-700"><ShieldCheck className="w-6 h-6" /></div>
        <div>
          <h2 className="text-2xl font-bold text-[#0F172A]">App Store Readiness</h2>
          <p className="text-sm text-slate-500 mt-0.5">Confirm everything is green before you resubmit XLabelSync to the Shopify App Store.</p>
        </div>
      </div>

      {/* Overall status */}
      <div className={`rounded-xl p-4 border ${data.all_ok ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`} data-testid="readiness-overall">
        {data.all_ok ? (
          <p className="text-emerald-800 font-semibold flex items-center gap-2"><CheckCircle2 className="w-5 h-5" /> All server-side checks passed — you're ready to set your Partner Dashboard URLs and resubmit.</p>
        ) : (
          <p className="text-amber-800 font-semibold flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> Some checks need attention (see below).</p>
        )}
      </div>

      {/* Checklist */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
          <h3 className="font-semibold text-[#0F172A] text-sm">Server-side checks</h3>
        </div>
        <ul className="divide-y divide-slate-100">
          {data.checks.map(c => (
            <li key={c.id} className="flex items-start gap-3 px-5 py-3" data-testid={`readiness-check-${c.id}`}>
              {c.ok
                ? <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                : <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#0F172A]">{c.label}</p>
                <p className="text-xs text-slate-500 break-words">{c.detail}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Partner Dashboard URLs */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
        <div className="flex items-center gap-2">
          <Store className="w-4 h-4 text-[#0052FF]" />
          <h3 className="font-semibold text-[#0F172A] text-sm">Paste these into your Shopify Partner / Dev Dashboard → App setup → URLs</h3>
        </div>
        <p className="text-xs text-slate-500">{pd.note}</p>

        {[
          { key: 'app_url', label: 'App URL', val: pd.app_url },
          { key: 'redirect', label: 'Allowed redirection URL', val: pd.allowed_redirection_url },
        ].map(row => (
          <div key={row.key} data-testid={`readiness-url-${row.key}`}>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">{row.label}</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 font-mono text-[#0F172A] break-all">{row.val || '— not configured —'}</code>
              <button onClick={() => copy(row.key, row.val)} disabled={!row.val}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-[#0052FF] hover:bg-[#0040CC] text-white text-xs font-semibold disabled:opacity-40 transition-colors"
                data-testid={`readiness-copy-${row.key}`}>
                {copied === row.key ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
              </button>
            </div>
          </div>
        ))}

        <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-xs text-blue-900">
          <strong>Why this matters:</strong> Shopify's rejection (policy 1.2.1) happens when an installing merchant reaches Stripe. Pointing the <strong>App URL</strong> at the install endpoint above guarantees every App Store install is tagged "billed through Shopify" and only ever sees Shopify Billing.
        </div>
      </div>

      {/* Scopes + merchant count */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">OAuth scopes requested</p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {(data.required_scopes || []).map(s => (
              <span key={s} className="text-[11px] font-mono px-2 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">{s}</span>
            ))}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">App Store merchants (billed via Shopify)</p>
          <p className="text-3xl font-black text-[#0F172A] mt-1" data-testid="readiness-merchant-count">{data.shopify_app_store_merchants ?? 0}</p>
        </div>
      </div>

      <button onClick={load} className="text-sm font-semibold text-[#0052FF] hover:underline" data-testid="readiness-refresh">Re-run checks</button>
    </div>
  );
}
